import React from "react";
type HeadroomState = "unfixed" | "pinned" | "unpinned";
type Props = {
  children: React.ReactNode;
  disable?: boolean;
  disableInlineStyles?: boolean;
  pinStart?: number;
  onPin?: () => void;
  onUnfix?: () => void;
  onUnpin?: () => void;
};
function getClassName(state: HeadroomState, animated: boolean) {
  return ["headroom", `headroom--${state}`, animated ? "headroom--scrolled" : ""].filter(Boolean).join(" ");
}

// Minimum sustained scroll distance (px) before the nav reacts. Without this,
// sub-pixel jitter, momentum/trackpad scrolling, and browser scroll-anchoring
// produce tiny direction reversals that flip the nav in and out erratically.
// A larger up-tolerance ensures the nav only reappears on a deliberate scroll up.
const UP_TOLERANCE = 10;
const DOWN_TOLERANCE = 5;
export function Headroom(props: Props) {
  const {
    children,
    disable = false,
    pinStart = 0,
    onPin,
    onUnfix,
    onUnpin
  } = props;
  const [state, setState] = React.useState<HeadroomState>("unfixed");
  // The slide transition only applies between the two fixed states. Animating the
  // unfixed -> unpinned change would briefly slide the nav down from the top
  // before hiding it, so we snap instantly when first leaving the unfixed state.
  const [animated, setAnimated] = React.useState(false);
  const lastScrollY = React.useRef(0);
  // Reference point that resets whenever the scroll direction reverses; the nav
  // only changes state once movement past this pivot exceeds the tolerance.
  const pivotY = React.useRef(0);
  const lastDirection = React.useRef(0);
  const stateRef = React.useRef<HeadroomState>("unfixed");
  React.useEffect(() => {
    if (disable) {
      if (stateRef.current !== "unfixed") {
        stateRef.current = "unfixed";
        setState("unfixed");
        setAnimated(false);
        onUnfix?.();
      }
      return;
    }
    function setNextState(nextState: HeadroomState) {
      if (stateRef.current === nextState) {
        return;
      }

      // Only animate transitions among the fixed states; snap when entering or
      // leaving "unfixed" to avoid the nav flashing in as it slides off-screen.
      setAnimated(stateRef.current !== "unfixed" && nextState !== "unfixed");
      stateRef.current = nextState;
      setState(nextState);
      if (nextState === "pinned") {
        onPin?.();
      } else if (nextState === "unpinned") {
        onUnpin?.();
      } else {
        onUnfix?.();
      }
    }
    function handleScroll() {
      const scrollY = Math.max(window.scrollY, 0);

      // Determine direction; keep the previous direction when the position is unchanged.
      const direction = scrollY > lastScrollY.current ? 1 : scrollY < lastScrollY.current ? -1 : lastDirection.current;

      // On a direction reversal, reset the pivot to where the trend began so the
      // tolerance is measured from the turnaround point, not the last frame.
      if (direction !== lastDirection.current) {
        pivotY.current = lastScrollY.current;
        lastDirection.current = direction;
      }
      if (scrollY <= pinStart) {
        setNextState("unfixed");
      } else if (direction === -1 && pivotY.current - scrollY > UP_TOLERANCE) {
        setNextState("pinned");
      } else if (direction === 1 && scrollY - pivotY.current > DOWN_TOLERANCE) {
        setNextState("unpinned");
      }
      lastScrollY.current = scrollY;
    }
    lastScrollY.current = Math.max(window.scrollY, 0);
    pivotY.current = lastScrollY.current;
    lastDirection.current = 0;
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [disable, onPin, onUnfix, onUnpin, pinStart]);
  return <div className={getClassName(state, animated)}>{children}</div>;
}
export default Headroom;